import { useState } from 'react';
import { type ContactAddress, ChannelType } from 'src/api/generated';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiPlus } from 'react-icons/pi';
import { useAlert } from 'src/contexts/alert-context';
import {
  useCreateContactAddressMutation,
  useUpdateContactAddressMutation,
  useDeleteContactAddressMutation,
} from 'src/modules/communications/hooks/api/contact-addresses.mutation';
import { SlideDownAnimation } from 'src/modules/shared/components/atoms/animations/slide-down';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';
import { usePhoneContext } from 'src/contexts/phone-context';
import { useGlobalPhoneContext } from 'src/contexts/global-phone-context';
import { isValidPhoneNumber } from 'src/utils/validation/phone';
import {
  getActionTooltip,
  getQuickPhoneState,
  isActionDisabled,
} from 'src/modules/communications/utils/quick-phone.util';
import { getContactAddressChannelLabelKey } from 'src/modules/communications/utils/contact-address-channel.util';
import { COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY } from 'src/modules/communications/utils/view-preference.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { DetailsSectionReveal } from './details-section-reveal';
import { ContactAddressItem } from './contact-address-item';
import { EditableAddressField } from 'src/modules/communications/components/molecules/contacts/editable-address-field';
import { DetailsFieldListSkeleton } from './details-field-list-skeleton-parts';
import { isTestAddress } from 'src/modules/communications/utils/test-conversation.util';

interface ContactAddressesSectionProps {
  addresses: ContactAddress[];
  loading?: boolean;
  contactId: number;
  error?: Error | null;
}

export const ContactAddressesSection: React.FC<
  ContactAddressesSectionProps
> = ({ addresses, loading = false, contactId, error = null }) => {
  const { t } = useI18n();
  const titleClassName = 'text-xs sm:text-xs uppercase text-secondary-500';
  const isSmallScreen = useMediaQuery(COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY);
  const { showAlert } = useAlert();
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({
    channelType: ChannelType.PHONE,
    address: '',
  });
  const [processingAddressId, setProcessingAddressId] = useState<number | null>(
    null
  );

  const { provider: callProvider, loading: providerLoading } =
    useGlobalCallProviderContext();
  const { phoneState } = usePhoneContext();
  const { setGlobalPhoneVisible } = useGlobalPhoneContext();

  const quickPhoneState = getQuickPhoneState(callProvider, phoneState);
  const hasManagedTestAddress = addresses.some((address) =>
    isTestAddress(address.address)
  );

  const handleCall = (phoneNumber: string) => {
    if (providerLoading || !callProvider) return;
    if (callProvider.loading || !quickPhoneState.isReady) return;

    setGlobalPhoneVisible(true);
    void callProvider.callActions.startCall({ phoneNumber });
  };

  const { createAddress, loading: creating } = useCreateContactAddressMutation(
    () => {
      setIsAddingAddress(false);
      setNewAddress({ channelType: ChannelType.PHONE, address: '' });
    },
    (error) => {
      console.error('Failed to add address:', error);
      showAlert(
        t('communications.contactPanel.contact.addresses.addError'),
        'error'
      );
    }
  );

  const { updateAddress, loading: updating } = useUpdateContactAddressMutation(
    () => {
      setProcessingAddressId(null);
    },
    (error) => {
      console.error('Failed to update address:', error);
      showAlert(
        t('communications.contactPanel.contact.addresses.updateError'),
        'error'
      );
      setProcessingAddressId(null);
    }
  );

  const { deleteAddress, loading: deleting } = useDeleteContactAddressMutation(
    () => {
      setProcessingAddressId(null);
    },
    (error) => {
      console.error('Failed to delete address:', error);
      showAlert(
        t('communications.contactPanel.contact.addresses.deleteError'),
        'error'
      );
      setProcessingAddressId(null);
    }
  );

  const handleUpdateAddress = async ({
    id,
    data,
  }: {
    id: number;
    data: Partial<ContactAddress>;
  }) => {
    setProcessingAddressId(id);
    await updateAddress({ id, data });
  };

  const handleDeleteAddress = async (addressId: number) => {
    setProcessingAddressId(addressId);
    await deleteAddress(addressId);
  };

  const renderAddButton = () => (
    <Button
      variant="neutral"
      appearance="soft"
      size="sm"
      isIconOnly
      onClick={() => setIsAddingAddress(true)}
      className="flex items-center gap-1"
    >
      <PiPlus className="w-4 h-4" />
    </Button>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <DetailsFieldListSkeleton
          fieldCount={2}
          variant="address"
          revealCount={1}
        />
      );
    }

    if (error) {
      return (
        <ErrorState
          title={t('communications.contactPanel.contact.addresses.error.title')}
          description={t(
            'communications.contactPanel.contact.addresses.error.description'
          )}
        />
      );
    }

    const sortedAddresses = [...addresses].sort((a, b) => {
      const order = [
        ChannelType.PHONE,
        ChannelType.EMAIL,
        ChannelType.WHATSAPP,
      ];
      return order.indexOf(a.channelType) - order.indexOf(b.channelType);
    });

    const addressItems = sortedAddresses.map((address) => {
      const isCallableChannel =
        address.channelType === ChannelType.PHONE ||
        address.channelType === ChannelType.WHATSAPP;
      const isValidPhone =
        isCallableChannel && isValidPhoneNumber(address.address);
      const isUpdating =
        processingAddressId === address.id && (updating || deleting);
      const isManagedTestAddress = isTestAddress(address.address);

      return (
        <ContactAddressItem
          key={address.id}
          address={address}
          label={t(getContactAddressChannelLabelKey(address.channelType))}
          labelSize={isSmallScreen ? 'xs' : 'sm'}
          loading={isUpdating}
          onSave={async ({ address: newAddress }) => {
            await handleUpdateAddress({
              id: address.id,
              data: { address: newAddress },
            });
          }}
          onDelete={() => handleDeleteAddress(address.id)}
          onAction={isCallableChannel ? handleCall : undefined}
          actionDisabled={
            isActionDisabled(address.channelType, quickPhoneState) ||
            !isValidPhone
          }
          actionLoading={quickPhoneState.isLoading}
          actionTooltip={getActionTooltip(
            address.channelType,
            quickPhoneState,
            isValidPhone
          )}
          readOnlyTooltip={
            isManagedTestAddress
              ? t(
                  'communications.testConversations.contactAddress.readOnlyTooltip'
                )
              : undefined
          }
        />
      );
    });

    return (
      <div className="flex flex-col gap-3 px-1">
        <SlideDownAnimation
          isVisible={isAddingAddress && !hasManagedTestAddress}
        >
          <EditableAddressField
            address={newAddress.address}
            channelType={newAddress.channelType}
            onSave={async ({ address, channelType }) => {
              await createAddress({
                contactId,
                address,
                channelType: channelType ?? ChannelType.PHONE,
              });
            }}
            loading={creating}
            onCancel={() => {
              setIsAddingAddress(false);
              setNewAddress({
                channelType: ChannelType.PHONE,
                address: '',
              });
            }}
            size="md"
            startEditing
            allowChannelTypeChange
          />
        </SlideDownAnimation>

        {sortedAddresses.length === 0 ? (
          <EmptyState
            description={t(
              'communications.contactPanel.contact.addresses.empty'
            )}
          />
        ) : (
          <DetailsSectionReveal items={addressItems} initiallyVisible={2} />
        )}
      </div>
    );
  };

  return (
    <ExpandableSection
      title={t('communications.contactPanel.contact.addresses.title')}
      titleClassName={titleClassName}
      className="bg-neutral-50/40"
      actions={
        !loading && !error && !hasManagedTestAddress
          ? renderAddButton()
          : undefined
      }
      contentClassName="max-h-none"
      compactHeaderOnMobile
    >
      {renderContent()}
    </ExpandableSection>
  );
};
